import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import {
  FieldErrorsImpl,
  FieldValues,
  SubmitHandler,
  useForm,
} from "react-hook-form";

import { postNewsletterSignUp } from "@/api/newsletter-sign-up";
import { InputText, LoadingSpinner, Text } from "@/components/elements";
import { Icon } from "@/components/elements/Icon";
import { ServerError } from "@/components/ServerError";
import { TermsAndConditions } from "@/components/Translation";
import useLocaleHelper from "@/hooks/useLanguageHelper";
import { GTM } from "@/lib/gtm";
import { NewsletterData } from "@/types/types";
import setSchema, { email } from "@/utils/validation";

import newsletter from "./newsletter.module.css";

export const NewsletterForm = () => {
  const { t } = useTranslation("footer");
  const { mutate, error, isSuccess, isLoading } =
    useMutation(postNewsletterSignUp);
  const { currentLanguage, currentCountry } = useLocaleHelper();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewsletterData>({
    mode: "onSubmit",
    defaultValues: {},
    criteriaMode: "all",
    shouldFocusError: true,
    resolver: yupResolver(
      setSchema({ ...email(t("error_email_only", { ns: "common" })) })
    ),
  });

  const onSubmit: SubmitHandler<NewsletterData> = async ({ email }) => {
    mutate(
      {
        email,
        language: currentLanguage,
        shop_locale: currentCountry,
      },
      { onSuccess: GTM.newsletterSignup }
    );
  };

  return (
    <form
      className={newsletter.form}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <Text as="h3" size="l">
        {t("newsletter.title")}
      </Text>

      <Text className={newsletter.copy}>
        {isSuccess ? t("newsletter.confirmation") : t("newsletter.description")}
      </Text>

      <div className={newsletter.inputContainer}>
        <InputText
          key="email-field"
          id="email-field"
          name="email"
          label={t("newsletter.placeholder")}
          type="email"
          version="box"
          theme="dark"
          register={register}
          errors={errors as FieldErrorsImpl<FieldValues>}
          hideLabel
          placeholder={t("newsletter.placeholder")}
        />
        {isLoading || isSuccess ? (
          <div className={newsletter.state}>
            {isSuccess && <Icon type="checkmark" />}
            {isLoading && <LoadingSpinner />}
          </div>
        ) : (
          <button className={newsletter.submit} type="submit">
            <Icon type="arrow_right" />
          </button>
        )}
        <ServerError error={error} className={newsletter.error} />
      </div>

      <Text size="xxs" className={newsletter.terms}>
        <TermsAndConditions />
      </Text>
    </form>
  );
};
